import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormLabel, FormGroup, FormControl, FilledInput, TextField, Button, IconButton, Icon, Tooltip } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useLocation, Link } from 'react-router-dom';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import NotFound from '../../common/not-found';
import DateRange from '../../common/date-range';
import ViewTitle from '../../common/view-title';
import EditPrices from '../components/edit-prices';
import SalePriceCreate from '../components/sale-price-create';
import SalePriceEdit from '../components/sale-price-edit';
import DownloadExcel from '../../common/download-excel';

import AuthContext from '../../context';
import { del, get, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingBottom: 20
    },
    tableContainer: {
        width: '100%'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 20
    },
    search: {
        marginRight: 15,
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none'
        }
    },
    create: {
        marginLeft: 15,
        backgroundColor: '#198754',
        borderColor: '#198754',
        color: '#FFF',
        '&:hover': {
            backgroundColor: '#198754',
            borderColor: '#198754',
            boxShadow: 'none'
        }
    },
    download: {
        backgroundColor: '#6E6E6E',
        borderColor: '#6E6E6E',
        '&:hover': {
            backgroundColor: '#6E6E6E',
            borderColor: '#6E6E6E',
            boxShadow: 'none'
        }
    }
}));

const defProduct = { id: 0, name: 'All products' };

const SalePrice = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editModal, setEditModal] = useState({ open: false, price: {} });
    const [selectedPrices, setSelectedPrices] = useState([]);
    const [excelData, setExcelData] = useState([]);
    const [state, setState] = useState({
        loading: true,
        data: [],
        products: [],
        filter: {
            startDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            dateRange: `${dayjs().subtract(1, 'day').format('M/DD/YYYY')} - ${dayjs().format('M/DD/YYYY')}`,
            product: defProduct
        },
        rangeModal: {
            open: false,
            range: [
                {
                    startDate: new Date(dayjs().subtract(1, 'day').toDate()),
                    endDate: new Date(),
                    key: 'main'
                }
            ]
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Ticket sale price';

        validatePermissions();
    }, []);

    useEffect(() => {
        const newExcelData = [];

        state.data.forEach(item => newExcelData.push({
            date: item.date,
            product: item.product_type_ref.product.name,
            type: item.product_type_ref.type.name,
            price: item.price,
            sede: item.sede.name
        }));

        setExcelData(newExcelData);
    }, [state.data]);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/ticketSalePrice', state.filter);
        if (response.status === 200) {
            setState(prev => ({ ...prev, loading: false, data: response?.data ?? [], products: response?.products ?? [] }));
        } else {
            console.log('[SalePrice] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const search = async () => {
        showLoader(true);
        const response = await get('/ticketSalePrice/search', state.filter);
        if (response.status === 200) {
            setState(prev => ({ ...prev, data: response?.data }));
            if (!response?.data?.length) showToast('There are no prices with these filters', 'warning');
        } else {
            console.log('[SalePrice] search error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const validateForm = (option, price) => {
        const { idticket_sale_price } = price;

        switch (option) {
            case 'delete':
                if (idticket_sale_price === 0) {
                    showToast('Select some price', 'error');
                    return false;
                }
                const cond = window.confirm(`Are you sure you want to delete this price?`);

                return cond;
            default:
                break;
        }
    }

    const onEdit = price => setEditModal({ price, open: true });

    const destroy = async price => {
        const { idticket_sale_price } = price;
        const cond = validateForm('delete', price);

        if (cond) {
            showLoader(true);
            const response = await del(`/ticketSalePrice/${idticket_sale_price}`);
            if (response.status === 200) {
                const prices = state.data.filter((value) => idticket_sale_price !== value.idticket_sale_price);

                setState(prev => ({ ...prev, data: prices }));
                showToast(response.message);
            } else {
                console.log('[SalePrice] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const multipleUpdate = async value => {
        showLoader(true);
        const response = await put(`/ticketSalePrice/multipleUpdate`, { value, prices: selectedPrices });
        if (response.status === 200) {
            search();
            showToast(response.message);
        } else {
            console.log('[SalePrice] multipleUpdate error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleDateChange = item => {
        const { startDate, endDate } = item.main;
        const dateRange = `${dayjs(startDate.toISOString()).format('MM/DD/YYYY')} - ${dayjs(endDate.toISOString()).format('MM/DD/YYYY')}`;
        setState(prev => ({
            ...prev,
            filter: {
                ...prev.filter,
                dateRange,
                startDate: dayjs(startDate.toISOString()).format('YYYY-MM-DD'),
                endDate: dayjs(endDate.toISOString()).format('YYYY-MM-DD')
            },
            rangeModal: { ...prev.rangeModal, range: [item.main] }
        }));
    }

    const handleModal = () => {
        setState(prev => ({ ...prev, rangeModal: { ...prev.rangeModal, open: !prev.rangeModal.open } }));
    }

    const handleAutocomplete = (key, value) => setState(prev => ({ ...prev, filter: { ...prev.filter, [key]: value } }));

    const valueGetter = ({ field, row }) => row?.product_type_ref[field]?.name ?? '?';
    const getDateField = params => dayjs(params.value).format('MM/DD/YYYY');
    const getValueField = params => `$ ${params.value}`;

    const renderCell = ({ row }) => {
        return (
            <>
                {
                    state.permissions?.update_attr &&
                    <Tooltip title='Edit' placement='top'>
                        <IconButton onClick={() => onEdit(row)}>
                            <Icon>{'edit'}</Icon>
                        </IconButton>
                    </Tooltip>
                }
                {
                    state.permissions?.delete_attr &&
                    <Tooltip title='Delete' placement='top'>
                        <IconButton onClick={() => destroy(row)}>
                            <Icon>{'delete'}</Icon>
                        </IconButton>
                    </Tooltip>
                }
                <Link to={`/history/prices/${row.idticket_sale_price}`} target='_blank'>
                    <Tooltip title='Show history' placement='top'>
                        <IconButton>
                            <Icon>{'history'}</Icon>
                        </IconButton>
                    </Tooltip>
                </Link>
            </>
        );
    }

    const columns = [
        { field: 'date', headerName: 'Date', flex: 1, minWidth: 150, valueGetter: getDateField },
        { field: 'product', headerName: 'Product', flex: 1, minWidth: 180, valueGetter },
        { field: 'type', headerName: 'Type', flex: 1, minWidth: 180, valueGetter },
        { field: 'price', headerName: 'Price', flex: 1, minWidth: 120, valueGetter: getValueField },
        { field: 'actions', headerName: 'Actions', flex: 1, minWidth: 120, renderCell }
    ];

    const excelColumns = [
        { label: 'Date', value: 'date' },
        { label: 'Product', value: 'product' },
        { label: 'Type', value: 'type' },
        { label: 'Price', value: 'price' },
        { label: 'Sede', value: 'sede' }
    ];

    if (state.loading) return <Loader />;
    if (!state.permissions.read_attr) return <NotFound />;

    return (
        <div className={classes.root}>
            <ViewTitle sedeFlag title='Sale prices' />
            <Grid container>
                <Grid item xs={12} md={6} lg={4}>
                    <FormGroup onClick={handleModal}>
                        <FormLabel>{'Date range'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                type='text'
                                placeholder='Date range'
                                value={state.filter.dateRange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Product'}</FormLabel>
                        <Autocomplete
                            options={state.products}
                            value={state.filter.product}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleAutocomplete('product', value)}
                            renderInput={params => <TextField {...params} placeholder='Product' />}
                        />
                    </FormGroup>
                </Grid>
            </Grid>

            <div className={classes.buttonContainer}>
                <Button variant='contained' className={classes.search} onClick={search}>{'Search'}</Button>
                {
                    state.permissions?.update_attr &&
                    <EditPrices prices={selectedPrices} update={multipleUpdate} />
                }
                {
                    state.permissions?.create_attr &&
                    <Button variant='contained' className={classes.create} onClick={() => setShowCreateModal(true)}>{'Create'}</Button>
                }
            </div>

            <div className={classes.buttonContainer}>
                <DownloadExcel fileName='ticket-sale-prices-report' sheetName='Sale prices report' data={excelData} columns={excelColumns} onClick={search} onReset={() => { }} />
            </div>

            <div className={classes.tableContainer}>
                <DataGrid
                    autoHeight
                    rows={state.data}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    getRowId={row => row.idticket_sale_price}
                    checkboxSelection
                    disableSelectionOnClick
                    onSelectionModelChange={prices => setSelectedPrices(prices)}
                />
            </div>

            <DateRange
                open={state.rangeModal.open}
                range={state.rangeModal.range}
                onClose={handleModal}
                onChange={handleDateChange}
            />

            <SalePriceCreate
                open={showCreateModal}
                onClose={(refresh = false) => {
                    (refresh === true) && search();
                    setShowCreateModal(false);
                }}
                products={state.products}
            />

            <SalePriceEdit
                open={editModal.open}
                onClose={(refresh = false) => {
                    (refresh === true) && search();
                    setEditModal(prev => ({ ...prev, open: false }));
                }}
                price={editModal.price}
            />
        </div>
    );
}

export default SalePrice;